import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PROFILE_KEY } from "../lib/me";
import { emptyDraft, toBody } from "../lib/profileDraft";
import { ProfileFields } from "../components/ProfileFields";
import type { Profile } from "../lib/types";
import { Button, Card, DiamondMark } from "../components/ui";

/**
 * Signup wizard step 2 (spec §8.1): display name required, phone optional.
 *
 * Shares its draft shape and its field set with the edit page (`Profile`), both
 * of which PUT the same replacing endpoint — see `profileDraft` for why that
 * has to be one body builder rather than two.
 */
export function ProfileWizard() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState(emptyDraft);

  const save = useMutation({
    mutationFn: () => api.put<Profile>("/me/profile", toBody(draft)),
    // By constant, not by literal: a key that drifts here leaves a coach
    // staring at the wizard they just finished.
    onSuccess: () => qc.invalidateQueries({ queryKey: PROFILE_KEY }),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (draft.displayName.trim()) save.mutate();
  };

  return (
    <div className="mx-auto max-w-md animate-scale-in">
      <Card className="space-y-5 p-6 sm:p-8">
        <div className="space-y-1 text-center">
          <DiamondMark className="mx-auto mb-3 h-11 w-11" />
          <h1 className="font-display text-xl font-bold tracking-tight text-navy-950">
            Finish setting up your account
          </h1>
          <p className="text-sm text-slate-500">
            Just a couple of details so organizers know who you are.
          </p>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <ProfileFields draft={draft} setDraft={setDraft} autoFocus />
          <Button type="submit" disabled={save.isPending} size="lg" className="w-full">
            {save.isPending ? "Saving…" : "Continue"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
